import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mantine/core'
import dayjs from 'dayjs'

import { DashboardTitleRow } from '../DashboardCard/DashboardCard'

import classes from './Upcoming.module.scss'
import { useAuth } from '@percent/workplace-giving/common/hooks/useAuth/useAuth'
import { getAccountFromAuthState } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import upcomingImg from '@percent/workplace-giving/assets/images/upcoming-calendar.png'
import starsImg from '@percent/workplace-giving/assets/images/stars.png'
import { ButtonText, Text } from '@percent/lemonade'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { getAccountOpportunities } from '@percent/workplace-giving/api/opportunity/getAccountOpportunities/getAccountOpportunities'
import { useLogger, useQuery } from '@percent/workplace-giving/common/hooks'
import { SkeletonLoader } from './SkeletonLoader/SkeletonLoader'
import { UpcomingItem } from './UpcomingItem/UpcomingItem'
import { UpcomingGivingActionItem } from './UpcomingGivingActionItem/UpcomingGivingActionItem'
import { useUpcomingGivingActions } from './useUpcomingGivingActions'
import { GivingActionCompletionModal } from '@percent/workplace-giving/common/components/GivingActionCompletionModal/GivingActionCompletionModal'
import { useGivingActionConfetti } from '@percent/workplace-giving/common/components/GivingActionConfetti/useGivingActionConfetti'
import { useGivingActionLogEvents } from '@percent/workplace-giving/common/components/LoggedDaysCalendar/useGivingActionLogEvents'
import { useGivingActionLogging } from '@percent/workplace-giving/common/hooks/useGivingActionLogging/useGivingActionLogging'

export const UPCOMING_COUNT = 3

export function Upcoming({ removeTab = false }: { removeTab?: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { logError } = useLogger()

  const authState = useAuth()
  const account = getAccountFromAuthState(authState.state)!
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isFirstLogin = useMemo(() => !account?.preferredName, [])

  const endDate = useMemo(() => new Date().toISOString(), [])

  const { data, isPending, error } = useQuery(
    [
      'getAccountOpportunitiesCount',
      {
        pageSize: UPCOMING_COUNT,
        participationStatus: ['registered', 'confirmed', 'waitlisted'],
        endDateGt: endDate,
        direction: 'ASC',
        type: 'event'
      }
    ],
    getAccountOpportunities,
    { staleTime: 1000 * 60 }
  )

  const { givingActions, isPending: isGivingActionsPending } = useUpcomingGivingActions()
  const { fire, confetti } = useGivingActionConfetti()
  const {
    selectedRow,
    selectedDate,
    loggingRowId,
    completedRowIds,
    isLoggingAction,
    setSelectedDate,
    handleViewAction,
    handleLogAction,
    handleConfirmCompletion,
    handleCloseModal
  } = useGivingActionLogging({ onLogCreated: fire })
  const { loggedDates } = useGivingActionLogEvents({ row: selectedRow })

  if (error) {
    logError(error)

    return null
  }

  const noUpcoming = data?.totalResults ? data?.totalResults <= 0 : true
  const hasGivingActions = givingActions.length > 0
  const hasOpportunities = !isFirstLogin && !noUpcoming
  const isLoading = isPending || isGivingActionsPending

  const givingActionItems = givingActions.map(row => (
    <UpcomingGivingActionItem
      key={row.id}
      row={row}
      onLogAction={handleLogAction}
      onViewAction={handleViewAction}
      isLogging={loggingRowId === row.id}
      justCompleted={completedRowIds.has(row.id)}
    />
  ))

  return (
    <>
      {isLoading && <SkeletonLoader />}
      {!isLoading && !hasGivingActions && (isFirstLogin || data?.totalResults === 0) ? (
        <Box className={classes.Wrapper} data-testid="no-upcoming-wrapper">
          {removeTab && !isFirstLogin && !noUpcoming && (
            <DashboardTitleRow title={t('workplace_giving.upcoming.title')} seeAllHref={AppRoute.MY_OPPORTUNITY} />
          )}

          <Box className={classes.Banner}>
            <img src={starsImg} alt="" />
            <img src={upcomingImg} alt="" />
          </Box>

          {isFirstLogin ? (
            <>
              <Text className={classes.Title}>{t('workplace_giving.upcoming.startVolunteering.title')}</Text>
              <Text className={classes.Description}>
                {t('workplace_giving.upcoming.startVolunteering.description')}
              </Text>
            </>
          ) : (
            <>
              <Text className={classes.Title}>{t('workplace_giving.upcoming.keepVolunteering.title')}</Text>
              <Text className={classes.Description}>{t('workplace_giving.upcoming.keepVolunteering.description')}</Text>
            </>
          )}

          <Box className={classes.LinkWrapper}>
            <ButtonText onClick={() => navigate(AppRoute.VOLUNTEER)}>
              {t('workplace_giving.upcoming.exploreBtn')}
            </ButtonText>
          </Box>
        </Box>
      ) : null}

      {!isLoading && (hasOpportunities || hasGivingActions) && (
        <Box className={classes.Wrapper}>
          {removeTab && (
            <DashboardTitleRow title={t('workplace_giving.upcoming.title')} seeAllHref={AppRoute.MY_OPPORTUNITY} />
          )}

          <Box data-testid="upcoming-list">
            {hasOpportunities &&
              data?.data?.map(item => (
                <UpcomingItem key={`${item.registrationDate}-${item.opportunity.id}`} {...item} />
              ))}
            {givingActionItems}
          </Box>
        </Box>
      )}

      <GivingActionCompletionModal
        open={!!selectedRow}
        row={selectedRow}
        selectedDate={selectedDate}
        onSelectedDateChange={setSelectedDate}
        onClose={handleCloseModal}
        onConfirm={handleConfirmCompletion}
        loading={isLoggingAction}
        loggedDates={loggedDates}
        minDate={selectedRow?.completableFrom ? dayjs(selectedRow.completableFrom).toDate() : undefined}
        maxDate={dayjs().endOf('day').toDate()}
      />
      {confetti}
    </>
  )
}
